import type { ButtonHTMLAttributes, ReactNode } from "react";

import { cx } from "../lib/cx";
import { Spinner } from "./Spinner";

export type ClayButtonVariant = "primary" | "secondary" | "ghost" | "danger";
export type ClayButtonSize = "sm" | "md";

export interface ClayButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ClayButtonVariant;
  size?: ClayButtonSize;
  loading?: boolean;
  fullWidth?: boolean;
  leftIcon?: ReactNode;
}

const VARIANTS: Record<ClayButtonVariant, string> = {
  primary: "accent-gradient text-white shadow-clay hover:brightness-105",
  secondary: "bg-surface text-text shadow-clay hover:text-accent",
  ghost: "bg-transparent text-muted hover:text-accent",
  danger: "bg-rose-500 text-white shadow-clay hover:brightness-105",
};

const SIZES: Record<ClayButtonSize, string> = {
  sm: "px-3.5 py-2 text-sm",
  md: "px-5 py-3 text-sm",
};

export function ClayButton({
  variant = "primary",
  size = "md",
  loading = false,
  fullWidth = false,
  leftIcon,
  className,
  children,
  disabled,
  type = "button",
  ...rest
}: ClayButtonProps) {
  return (
    <button
      {...rest}
      type={type}
      disabled={disabled || loading}
      className={cx(
        "inline-flex items-center justify-center gap-2 rounded-control font-semibold transition-all duration-150",
        "active:clay-pressed disabled:cursor-not-allowed disabled:opacity-60 disabled:active:shadow-clay",
        VARIANTS[variant],
        SIZES[size],
        fullWidth && "w-full",
        className,
      )}
    >
      {loading ? <Spinner size="sm" className="text-current" /> : leftIcon}
      <span>{children}</span>
    </button>
  );
}
